import type { FAQItem } from '../components/FAQSection';

/** Content collections that share the ArticleLayout + SSG/sitemap/llms pipeline. */
export type ContentCollection = 'guides' | 'use-cases' | 'alternatives';

export interface ContentAuthor {
  name: string;
  role?: string;
  url?: string;
}

export interface HowToStep {
  name: string;
  text: string;
}

/** A single tool row used by "alternatives" listicles (drives ItemList schema). */
export interface AlternativeItem {
  name: string;
  bestFor: string;
  pricing: string;
  freeTier: boolean;
  url?: string;
}

export interface RelatedLink {
  label: string;
  to: string;
}

/**
 * A cornerstone content article (guide, use-case, or alternatives listicle).
 *
 * Long-form prose lives in a sibling `.md` file imported via `?raw`; everything
 * the build pipeline, schema, and navigation needs is typed here so new pages
 * auto-register into routing, the sitemap, and llms.txt.
 */
export interface Article {
  collection: ContentCollection;
  slug: string;
  /** Visible H1. */
  title: string;
  /** <title> tag — keep ≤ 60 chars where possible. */
  metaTitle: string;
  /** Meta description — keep ≤ 160 chars. */
  description: string;
  /** Small category label above the H1. */
  eyebrow: string;
  /** One-sentence summary under the H1 (also used in llms.txt + index cards). */
  heroSubtitle: string;
  /** Markdown body (rendered with `marked`). */
  body: string;
  faqs?: FAQItem[];
  related?: RelatedLink[];
  author?: ContentAuthor;
  /** ISO date. */
  datePublished: string;
  /** ISO date. */
  dateModified: string;
  readingTime?: string;
  schemaType?: 'Article' | 'HowTo';
  howToSteps?: HowToStep[];
  ogImage?: string;
  /** Present on alternatives listicles → drives ItemList structured data. */
  itemList?: AlternativeItem[];
}

export const SITE_URL = 'https://pollenate.dev';

export const DEFAULT_AUTHOR: ContentAuthor = {
  name: 'The Pollenate Team',
  role: 'Customer feedback experts at Pollenate',
  url: 'https://pollenate.dev/about',
};

export function collectionBasePath(collection: ContentCollection): string {
  return `/${collection}`;
}

export function articlePath(article: Pick<Article, 'collection' | 'slug'>): string {
  return `${collectionBasePath(article.collection)}/${article.slug}`;
}

export function collectionLabel(collection: ContentCollection): string {
  switch (collection) {
    case 'use-cases':
      return 'Use cases';
    case 'alternatives':
      return 'Alternatives';
    default:
      return 'Guides';
  }
}
